/**
 * Doodle tabs (blocks/accordion.liquid with "Doodle tabs" on).
 *
 * Turns the accordion's rows into tabs: one hand-drawn tab button per row (labelled with
 * the row's heading), and only the selected row stays open. The rows are ordinary
 * <details> elements, so without this script the block still works as an accordion.
 *
 * The theme's accordion-custom script resets rows to closed when it starts up and when the
 * screen crosses the mobile breakpoint, which can happen after this runs. Each row's
 * `toggle` event is watched so the selected row is re-opened and the folder never ends up
 * with no page showing.
 */
class DoodleTabs extends HTMLElement {
  connectedCallback() {
    const rows = Array.from(this.querySelectorAll(':scope > accordion-custom > details'));
    if (rows.length === 0 || this.querySelector(':scope > .doodle-tabs__list')) return;

    const list = document.createElement('div');
    list.className = 'doodle-tabs__list';
    list.setAttribute('role', 'tablist');
    list.style.setProperty('--doodle-tab-count', String(rows.length));

    const tabs = rows.map((row, index) => {
      row.addEventListener('toggle', () => {
        if (!row.open && index === this.selected) row.open = true;
      });

      const summary = row.querySelector(':scope > summary');
      const content = row.querySelector(':scope > .details-content');
      const id = `${this.closest('[class*="accordion--"]')?.className.match(/accordion--([\w-]+)/)?.[1] ?? 'tabs'}-${index}`;

      const tab = document.createElement('button');
      tab.type = 'button';
      tab.className = 'doodle-tabs__tab';
      tab.setAttribute('role', 'tab');
      tab.id = `doodle-tab-${id}`;
      tab.textContent = summary?.textContent.trim() ?? `Tab ${index + 1}`;
      tab.addEventListener('click', () => this.#select(index));
      tab.addEventListener('keydown', (event) => this.#onKeydown(event, index));

      if (content) {
        content.id = content.id || `doodle-panel-${id}`;
        content.setAttribute('role', 'tabpanel');
        content.setAttribute('aria-labelledby', tab.id);
        tab.setAttribute('aria-controls', content.id);
      }
      list.append(tab);
      return tab;
    });

    this.prepend(list);
    this.tabs = tabs;
    this.rows = rows;
    this.setAttribute('data-ready', '');

    const initiallyOpen = rows.findIndex((row) => row.open);
    this.#select(initiallyOpen === -1 ? 0 : initiallyOpen, false);
  }

  #select(index, focus = false) {
    this.selected = index;
    this.rows.forEach((row, i) => {
      row.open = i === index;
    });
    this.tabs.forEach((tab, i) => {
      const selected = i === index;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
      if (selected && focus) tab.focus();
    });
  }

  #onKeydown(event, index) {
    const last = this.tabs.length - 1;
    let next = null;
    if (event.key === 'ArrowRight') next = index === last ? 0 : index + 1;
    if (event.key === 'ArrowLeft') next = index === 0 ? last : index - 1;
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = last;
    if (next === null) return;
    event.preventDefault();
    this.#select(next, true);
  }
}

if (!customElements.get('doodle-tabs')) {
  customElements.define('doodle-tabs', DoodleTabs);
}
